import { Injectable, inject, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { OAuthService, OAuthErrorEvent } from 'angular-oauth2-oidc';
import { BehaviorSubject, defer, filter, firstValueFrom, from, map, Observable, shareReplay, switchMap, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UsersService } from '../../../gen/endpoints/user-auth/users/users.service';
import { PermissionService } from '../services/permission.service';
import { PermissionsActions } from '../redux/actions/permissions.actions';
import { authCodeFlowConfig } from './auth.config';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
    private permissionsService = inject(PermissionService);
    private store = inject<Store<PermissionState>>(Store);
    private usersService = inject(UsersService);

    private oauthService: OAuthService;
    private router: Router;

    private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
    public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

    private userProfileSubject = new BehaviorSubject<any>(null);
    public userProfile$ = this.userProfileSubject.asObservable();

    private errorSubject = new BehaviorSubject<string | null>(null);
    public error$ = this.errorSubject.asObservable();

    /**
     * Эмитит первое значение только после завершения инициализации OAuth.
     * Нужен гардам, чтобы не отправлять на повторный логин вкладку,
     * у которой уже есть валидный токен.
     */
    private authReadySubject = new BehaviorSubject<boolean | null>(null);
    public authReady$: Observable<boolean> = this.authReadySubject.pipe(filter((v): v is boolean => v !== null));

    private isInitialLogin = true;
    private isSilentRefreshInProgress = false;
    private permissionsLoaded = false;
    private permissionsLoadingPromise: Promise<void> | null = null;
    private profileLoaded$?: Observable<any>;
    private destroy$ = new Subject<void>();
    readonly REDIRECT_URL_KEY = 'redirect_url';

    user$ = defer(() => this.getUserProfile()).pipe(
        map((profile) => Number(profile.info.sub)),
        switchMap((id) => this.usersService.getApiUsersMe()),
        shareReplay(1)
    );

    userName$ = this.user$.pipe(map((user) => `${user.firstName} ${user.middleName ? user.middleName + ' ' : ''}${user.lastName}`));
    userId$ = this.user$.pipe(map((user) => user.id));

    /**
     * Проверяет, есть ли у пользователя хотя бы одна из указанных ролей.
     * Данные берутся из API /api/users/me (поле roles).
     * @param roles - массив ролей для проверки (например, ['superadmin', 'admin'])
     * @returns Observable<boolean> - true если у пользователя есть хотя бы одна из указанных ролей
     */
    hasRole(roles: string[]): Observable<boolean> {
        return this.user$.pipe(
            map((user) => {
                const userRoles = user?.roles;
                if (!userRoles || !Array.isArray(userRoles)) {
                    return false;
                }
                return roles.some((role) => userRoles.includes(role));
            })
        );
    }

    constructor(oauthService: OAuthService, router: Router) {
        this.oauthService = oauthService;
        this.router = router;
        this.initialize();
    }

    private initialize(): void {
        // Чистим устаревший общий для всех вкладок ключ — теперь он в sessionStorage
        localStorage.removeItem(this.REDIRECT_URL_KEY);
        this.setupOAuthEvents();
        this.setupRouterEvents();
        this.initOAuth();
    }

    private setupOAuthEvents(): void {
        this.oauthService.events
            .pipe(
                filter((e) => e instanceof OAuthErrorEvent),
                takeUntil(this.destroy$)
            )
            .subscribe((e) => {
                this.handleOAuthError(e as OAuthErrorEvent);
            });

        this.oauthService.events
            .pipe(
                filter((e) => ['token_received', 'silently_refreshed'].includes(e.type)),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.handleTokenReceived();
            });

        this.oauthService.events
            .pipe(
                filter((e) => e.type === 'silent_refresh_error'),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.isSilentRefreshInProgress = false;
            });
    }

    private setupRouterEvents(): void {
        this.router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe((e: NavigationEnd) => {
                const url = e.urlAfterRedirects || e.url;
                if (this.shouldSaveRedirectUrl(url)) this.saveRedirectUrl(url);
            });
    }

    private async initOAuth(): Promise<void> {
        this.oauthService.configure(authCodeFlowConfig);
        // Страховка: если инициализация зависнет, гарды не должны ждать её вечно
        const readyFallback = setTimeout(() => this.markAuthReady(), 15000);
        try {
            await this.oauthService.loadDiscoveryDocumentAndTryLogin();
            this.oauthService.setupAutomaticSilentRefresh({ timeout: 10000, retry: 2 });
            const valid = this.oauthService.hasValidAccessToken();
            this.isAuthenticatedSubject.next(valid);

            if (valid) {
                const profile = await firstValueFrom(this.getUserProfile());
                this.userProfileSubject.next(profile);

                if (!this.permissionsLoaded) {
                    await this.loadPermissions();
                }
                this.isInitialLogin = false;
            }
        } catch {
            this.errorSubject.next('Ошибка инициализации OAuth');
        } finally {
            clearTimeout(readyFallback);
            this.markAuthReady();
        }
    }

    private markAuthReady(): void {
        if (this.authReadySubject.value === null) {
            this.authReadySubject.next(this.oauthService.hasValidAccessToken());
        }
    }

    private async handleTokenReceived(): Promise<void> {
        try {
            if (!this.isSilentRefreshInProgress && !this.permissionsLoaded) {
                await this.loadPermissions();
            }

            const profile = await firstValueFrom(this.getUserProfile());
            this.userProfileSubject.next(profile);
            this.isAuthenticatedSubject.next(true);
            this.markAuthReady();

            // Переход по сохранённому URL допустим только на странице колбэка.
            // Иначе продление токена (silently_refreshed) уводит пользователя
            // с открытой страницы на посторонний адрес.
            if (this.isInitialLogin && !this.isSilentRefreshInProgress && this.isOnAuthCallback()) {
                await this.navigateToRedirectUrl();
            }
            this.isInitialLogin = false;
        } catch {
            this.errorSubject.next('Ошибка при обработке токена');
        } finally {
            this.isSilentRefreshInProgress = false;
        }
    }

    private async loadPermissions(): Promise<void> {
        if (this.permissionsLoaded) {
            return;
        }
        if (this.permissionsLoadingPromise) {
            return this.permissionsLoadingPromise;
        }

        this.permissionsLoadingPromise = (async () => {
            try {
                const permissions = await this.permissionsService.getPermissions();
                this.store.dispatch(PermissionsActions.loadPermissionsSuccess({ permissions }));
                this.permissionsLoaded = true;
            } catch (error) {
                // Даже при ошибке, отмечаем разрешения загруженными, чтобы не зависнуть
                this.store.dispatch(PermissionsActions.loadPermissionsFailure({ error }));
                this.permissionsLoaded = true;
            } finally {
                this.permissionsLoadingPromise = null;
            }
        })();

        return this.permissionsLoadingPromise;
    }

    private handleOAuthError(error: OAuthErrorEvent): void {
        const params = error.params as any;

        // Специальная обработка ошибки invalid_nonce_in_state
        // Эта ошибка возникает когда в localStorage остались старые токены/nonce
        if (error.type === 'invalid_nonce_in_state') {
            this.clearOAuthStorage();
            this.router.navigate(['/']);
            this.errorSubject.next('Необходимо выполнить повторный вход. Пожалуйста, войдите снова.');
            return;
        }

        if (error.type === 'silent_refresh_timeout') {
            this.isSilentRefreshInProgress = true;
            this.oauthService.hasValidAccessToken() ? this.refreshToken() : this.login();
            return;
        }

        this.errorSubject.next(params?.error_description || `OAuth error: ${params?.error || 'Unknown'}`);

        if (['login_required', 'invalid_token'].includes(params?.error)) {
            params.error === 'login_required' ? this.login() : this.logout();
        }
    }

    /**
     * Очищает все OAuth данные из localStorage и sessionStorage
     * Используется при ошибках валидации nonce для "чистого" старта
     */
    private clearOAuthStorage(): void {
        const localStorageKeys = Object.keys(localStorage);
        const oauthPrefixes = ['nonce', 'PKCE', 'access_token', 'id_token', 'refresh_token', 'expires_at', 'state'];

        localStorageKeys.forEach(key => {
            if (oauthPrefixes.some(prefix => key.includes(prefix))) {
                localStorage.removeItem(key);
            }
        });

        const sessionStorageKeys = Object.keys(sessionStorage);
        sessionStorageKeys.forEach(key => {
            if (oauthPrefixes.some(prefix => key.includes(prefix))) {
                sessionStorage.removeItem(key);
            }
        });
    }

    private async navigateToRedirectUrl(): Promise<void> {
        await this.router.navigateByUrl(this.getRedirectUrl() ?? '/');
    }

    public login(redirectUrl?: string): void {
        this.saveRedirectUrl(redirectUrl);
        this.oauthService.initCodeFlow();
    }

    /**
     * URL возврата хранится в sessionStorage, а не в localStorage:
     * localStorage общий для всех вкладок, поэтому при открытии нескольких
     * заявок подряд в новых вкладках они перетирали адрес друг друга
     * и после авторизации все открывали одну и ту же запись.
     */
    public saveRedirectUrl(redirectUrl?: string): void {
        const url = redirectUrl ? this.safeDecode(redirectUrl) : window.location.pathname + window.location.search;
        sessionStorage.setItem(this.REDIRECT_URL_KEY, url);
    }

    public getRedirectUrl(): string | null {
        return sessionStorage.getItem(this.REDIRECT_URL_KEY);
    }

    private safeDecode(url: string): string {
        try {
            return decodeURIComponent(url);
        } catch {
            return url;
        }
    }

    private isOnAuthCallback(): boolean {
        return window.location.pathname.includes('signin-oidc');
    }

    public logout(): void {
        sessionStorage.removeItem(this.REDIRECT_URL_KEY);
        this.oauthService.logOut();
        this.isAuthenticatedSubject.next(false);
        this.userProfileSubject.next(null);
        this.permissionsLoaded = false;
        this.permissionsLoadingPromise = null;
        this.profileLoaded$ = undefined;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.isAuthenticatedSubject.complete();
        this.userProfileSubject.complete();
        this.errorSubject.complete();
    }

    public getAccessToken(): Observable<string> {
        return this.isAuthenticated$.pipe(map(() => this.oauthService.getAccessToken()));
    }

    public refreshToken(): void {
        this.oauthService.refreshToken();
    }

    public async getUserName(): Promise<string> {
        const profile: any = await firstValueFrom(this.getUserProfile());
        const user = await firstValueFrom(this.usersService.getApiUsersMe());
        return `${user.firstName} ${user.middleName ? user.middleName + ' ' : ''}${user.lastName}`;
    }

    private getUserProfile(): Observable<any> {
        if (!this.profileLoaded$) {
            this.profileLoaded$ = from(this.oauthService.loadUserProfile()).pipe(shareReplay(1));
        }
        return this.profileLoaded$;
    }

    private shouldSaveRedirectUrl(url: string): boolean {
        return !['logout', 'signout-callback-oidc', 'signin-oidc', 'no-access', 'notfound', 'signout'].some((ex) => url.includes(ex));
    }
}